import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import type { AddressDto, ILoqOptionDto, UserDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { Stars01Icon } from "@/components/Icons/Stars01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Pagination } from "@/components/Pagination/Pagination";
import { ProgressBar } from "@/components/ProgressBar/ProgressBar";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { MultiSelect } from "@/design-system/MultiSelect";
import { Notice } from "@/design-system/Notice";
import { SegmentGroup } from "@/design-system/SegmentGroup";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { Tiles } from "@/design-system/Tiles";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { useAddressQueries } from "@/queries/addresses/queryOptions";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

const ADDRESS_PAGE_SIZE = 15;
type AddressFilter = "all" | "unmapped";

export type ILoqFormValues = {
  serviceAddress: string;
  customerCode: string;
  username: string;
  password: string;
  lockGroup: ILoqOptionDto | null;
  realEstate: ILoqOptionDto | null;
  zone: ILoqOptionDto | null;
  supportUser: UserDto | null;
  mappings: Record<string, string[]>;
};

export function ILoqPage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const canManageIloq = hasPermission((x) => x.integrations.canManageILoq);
  const integrationQueries = useIntegrationQueries();
  const userQueries = useUserQueries();

  const {
    data: savedConfig,
    isFetching: isLoadingConfig,
    error: configError,
  } = useQuery({ ...integrationQueries.iloqConfiguration(), enabled: canManageIloq });
  const {
    data: adminUsers,
    isFetching: isLoadingUsers,
    error: usersError,
  } = useQuery({ ...userQueries.listAdmins(), enabled: canManageIloq });
  const disableMutation = integrationMutations.useEditIloqConfiguration();
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);

  if (!canManageIloq) {
    return <ErrorPage status={404} />;
  }

  if (configError || usersError) {
    return <ErrorPage error={configError || usersError} />;
  }

  if (isLoadingConfig || isLoadingUsers) {
    return <FullSizeLoader withPadding />;
  }

  const name = t("page.integration.iloq.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <Stack gap="md">
        {savedConfig?.enabled && (
          <Notice
            variant="success"
            icon={<CheckIcon />}
            message={t("page.integration.iloq.status.enabled")}
            action={{ label: t("page.integration.iloq.action.disable"), onClick: () => setIsDisableModalOpen(true) }}
          />
        )}
        {savedConfig?.serviceAddress && !savedConfig.enabled && (
          <Notice variant="neutral" message={t("page.integration.iloq.status.disabled")} />
        )}
        <ILoqForm
          defaultValues={{
            serviceAddress: savedConfig?.serviceAddress ?? "",
            customerCode: savedConfig?.customerCode ?? "",
            username: savedConfig?.username ?? "",
            password: "",
            lockGroup: null,
            realEstate: null,
            zone: null,
            supportUser: (adminUsers ?? []).find((u) => u.id === savedConfig?.supportUserId) ?? null,
            mappings: Object.fromEntries(
              (savedConfig?.securityAccessMappings ?? []).map((m) => [m.addressId, m.securityAccessIds]),
            ),
          }}
          adminUsers={adminUsers ?? []}
          hasSavedConfig={!!savedConfig?.serviceAddress}
          savedLockGroupId={savedConfig?.lockGroupId}
          savedRealEstateId={savedConfig?.realEstateId}
          savedZoneId={savedConfig?.zoneId}
        />
      </Stack>
      <ConfirmModal
        tone="warning"
        title={t("page.integration.iloq.disable-modal.title")}
        description={t("page.integration.iloq.disable-modal.description")}
        isOpened={isDisableModalOpen}
        onOpenChange={setIsDisableModalOpen}
        onReject={() => setIsDisableModalOpen(false)}
        onResolve={() =>
          disableMutation.mutate(
            { enabled: false, securityAccessMappings: [] },
            { onSuccess: () => setIsDisableModalOpen(false) },
          )
        }
        resolveBtnProps={{ text: t("page.integration.iloq.action.disable") }}
        isLoading={disableMutation.isPending}
      />
    </DocumentPage>
  );
}

/**
 * Inputs for the reusable iLOQ form. `projectId` scopes its requests, `enabledOnSave` controls the saved activation
 * state, and `onSaved` runs only after a successful mutation.
 */
export type ILoqFormProps = {
  defaultValues: ILoqFormValues;
  adminUsers: UserDto[];
  hasSavedConfig: boolean;
  savedLockGroupId: string | undefined;
  savedRealEstateId: string | undefined;
  savedZoneId: string | undefined;
  projectId?: string;
  enabledOnSave?: boolean;
  submitLabel?: string;
  onSaved?: () => void;
};

/** Renders the project-scoped iLOQ connection, options, address-mapping, and save workflow. */
export function ILoqForm({
  defaultValues,
  adminUsers,
  hasSavedConfig,
  savedLockGroupId,
  savedRealEstateId,
  savedZoneId,
  projectId,
  enabledOnSave = true,
  submitLabel,
  onSaved,
}: ILoqFormProps): React.ReactNode {
  const { t } = useTranslation();
  const addressQueries = useAddressQueries();
  const integrationQueries = useIntegrationQueries();
  const saveMutation = integrationMutations.useEditIloqConfiguration(projectId);

  const formMethods = useForm<ILoqFormValues>({ defaultValues });
  const { setValue } = formMethods;

  const [userAddressFilter, setUserAddressFilter] = useState<AddressFilter | null>(null);
  const [addressSearch, setAddressSearch] = useState("");
  const [addressPage, setAddressPage] = useState(0);

  const [serviceAddress, customerCode, username, password, lockGroup, realEstate, zone, mappings] = useWatch({
    name: ["serviceAddress", "customerCode", "username", "password", "lockGroup", "realEstate", "zone", "mappings"],
    control: formMethods.control,
  });

  const step1CanConnect =
    serviceAddress?.trim() && customerCode?.trim() && username?.trim() && (hasSavedConfig || password?.trim());

  // Built from the watched values, not read via getValues(): the React Compiler memoizes calls on
  // stable references, so a getValues-based helper kept returning the first render's empty values.
  const credentials = {
    serviceAddress: serviceAddress ?? "",
    customerCode: customerCode ?? "",
    username: username ?? "",
    password: password ?? "",
  };
  const credentialsUnchanged =
    credentials.serviceAddress === defaultValues.serviceAddress &&
    credentials.customerCode === defaultValues.customerCode &&
    credentials.username === defaultValues.username &&
    !credentials.password;

  // Connect is a query (not a mutation fired imperatively) so it can be triggered automatically
  // on mount when there's a saved config: mutate()-in-an-effect doesn't survive React StrictMode's
  // mount->cleanup->mount dance in dev (the observer can be torn down before it reports back,
  // leaving the button spinning forever even though the request actually succeeded). useQuery is
  // built to survive that. Manual (re)connect just calls refetch(), which works even when disabled.
  // Auto-connecting stops once the credentials are edited, so typing doesn't fire a request per keystroke.
  const connectQuery = useQuery({
    ...integrationQueries.iloqLockGroups(credentials, projectId),
    enabled: hasSavedConfig && credentialsUnchanged,
  });
  // The query key omits the password and can restore cached data for a previously used credential
  // identity. Track the complete credentials that last connected so edited values always require
  // an explicit reconnect before later steps become available.
  const [connectedCredentials, setConnectedCredentials] = useState(credentials);
  const [connectionRevision, setConnectionRevision] = useState(0);
  const lockGroups = connectQuery.data?.data ?? null;
  const connectError = connectQuery.isError;
  const step1Done =
    lockGroups !== null &&
    !connectError &&
    connectedCredentials.serviceAddress === credentials.serviceAddress &&
    connectedCredentials.customerCode === credentials.customerCode &&
    connectedCredentials.username === credentials.username &&
    connectedCredentials.password === credentials.password;

  const handleConnect = async () => {
    const attemptedCredentials = credentials;
    const result = await connectQuery.refetch();
    if (result.isSuccess) {
      setConnectedCredentials(attemptedCredentials);
      setConnectionRevision((revision) => revision + 1);
    }
  };

  useEffect(() => {
    if (!connectQuery.data) {
      return;
    }

    setValue("lockGroup", connectQuery.data.data.find((g) => g.id === savedLockGroupId) ?? null);
  }, [connectQuery.data, savedLockGroupId, setValue]);

  // Loading options auto-fires whenever a lock group is selected (auto-matched from the saved
  // config, or picked manually) instead of waiting for an explicit click. The connection revision
  // prevents cached options from an earlier password from completing this step after reconnecting.
  const optionsQuery = useQuery({
    ...integrationQueries.iloqOptions(lockGroup?.id ?? "", credentials, connectionRevision, projectId),
    enabled: step1Done && !!lockGroup,
  });
  const options = optionsQuery.data?.data ?? null;
  const step2Done = step1Done && !!lockGroup && optionsQuery.isSuccess;
  const step3Done = step2Done && !!realEstate && !!zone;

  useEffect(() => {
    if (!optionsQuery.data) {
      return;
    }

    setValue("realEstate", optionsQuery.data.data.realEstates.find((r) => r.id === savedRealEstateId) ?? null);
    setValue("zone", optionsQuery.data.data.zones.find((z) => z.id === savedZoneId) ?? null);
  }, [optionsQuery.data, savedRealEstateId, savedZoneId, setValue]);

  // Reset the address list's filter/page whenever a new options set loads. Adjusted during
  // render (React's recommended pattern for "reset state when a value changes") rather than in
  // an effect, so it doesn't cause an extra cascading render.
  const [addressFilterResetKey, setAddressFilterResetKey] = useState(optionsQuery.data);
  if (optionsQuery.data !== addressFilterResetKey) {
    setAddressFilterResetKey(optionsQuery.data);
    setUserAddressFilter(null);
    setAddressPage(0);
  }

  const { data: addressList } = useQuery({
    ...addressQueries.list({ limit: 1000, projectId }),
    enabled: step3Done,
  });
  const aopAddresses = useMemo(() => addressList?.items ?? [], [addressList]);
  const securityAccesses = useMemo(() => options?.securityAccesses ?? [], [options]);

  // The security-access list is identical for every row, but the *most likely* one differs per
  // address. Pre-sort a copy per address (most likely first) so each dropdown surfaces the
  // relevant options at the top. Recomputed only when the address/access set itself changes.
  const sortedAccessesByAddress = useMemo(() => {
    const byAddress = new Map<string, ILoqOptionDto[]>();
    for (const address of aopAddresses) {
      const sorted = securityAccesses
        .map((sa) => ({ sa, score: scoreSecurityMatch(address, sa) }))
        // Most likely first (Resident always ranks top); ties — notably the score-0
        // non-matches — fall back to a natural alphanumeric sort by name.
        .sort((a, b) => b.score - a.score || a.sa.name.localeCompare(b.sa.name, undefined, { numeric: true }))
        .map((x) => x.sa);
      byAddress.set(address.id, sorted);
    }

    return byAddress;
  }, [aopAddresses, securityAccesses]);

  // A stable identity for the currently-connected selection. The object refs for lockGroup/
  // realEstate/zone churn whenever the connect/options queries refetch in the background (the
  // auto-prefill effects above re-run and hand back a fresh `.find()`-ed object even when the id
  // is unchanged). Keying prematch on the ids instead keeps it stable across those refetches, so
  // it no longer re-fires and wipes the user's manual mapping edits.
  const selectionKey =
    step3Done && aopAddresses.length > 0 && securityAccesses.length > 0
      ? `${lockGroup?.id ?? ""}|${realEstate?.id ?? ""}|${zone?.id ?? ""}`
      : null;

  // Run prematch exactly once per distinct selection. Editing a mapping doesn't change the
  // selectionKey, so the user's manual changes are never clobbered; only actually connecting to a
  // different lock group / real estate / zone re-guesses. A ref (not state) tracks the last
  // prematched key so the guard doesn't trigger an extra cascading render.
  const prematchedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectionKey || prematchedKeyRef.current === selectionKey) {
      return;
    }
    prematchedKeyRef.current = selectionKey;

    // If the selection matches the saved config, the mappings loaded from it (via defaultValues)
    // are already correct — don't clobber them with fresh guesses.
    const isUsingSavedSelection =
      lockGroup?.id === savedLockGroupId && realEstate?.id === savedRealEstateId && zone?.id === savedZoneId;
    if (isUsingSavedSelection) {
      return;
    }

    setValue("mappings", prematch(aopAddresses, securityAccesses));
  }, [
    selectionKey,
    lockGroup,
    realEstate,
    zone,
    savedLockGroupId,
    savedRealEstateId,
    savedZoneId,
    aopAddresses,
    securityAccesses,
    setValue,
  ]);

  const totalAddresses = aopAddresses.length;
  const matchedCount = aopAddresses.filter((a) => !!(mappings ?? {})[a.id]?.length).length;
  const unmatchedCount = totalAddresses - matchedCount;
  const progressPercentage = totalAddresses ? Math.round((matchedCount / totalAddresses) * 100) : 0;

  // Once all three conditions are true the effect has run prematch; derive the default
  // filter from that without touching the ref (which can't be read during render).
  const prematchHasRun = selectionKey !== null;
  const addressFilter: AddressFilter = userAddressFilter ?? (prematchHasRun && unmatchedCount > 0 ? "unmapped" : "all");

  // Freeze which addresses satisfy the current filter/search, so that mapping an address (while
  // viewing "unmapped") doesn't yank its row out from under the user mid-interaction. The
  // snapshot only refreshes when the filter, search, or address set itself actually change —
  // not merely because `mappings` changed. Adjusted during render rather than in an effect, to
  // match the reset pattern used above and avoid an extra cascading render.
  const [addressListSnapshot, setAddressListSnapshot] = useState<{
    filter: AddressFilter;
    search: string;
    addresses: AddressDto[];
    ids: string[];
  } | null>(null);
  if (
    !addressListSnapshot ||
    addressListSnapshot.filter !== addressFilter ||
    addressListSnapshot.search !== addressSearch ||
    addressListSnapshot.addresses !== aopAddresses
  ) {
    const ids = aopAddresses
      .filter((a) => {
        const matchSearch = !addressSearch || a.location.toLowerCase().includes(addressSearch.toLowerCase());

        return matchSearch && (addressFilter === "all" || !(mappings ?? {})[a.id]?.length);
      })
      .map((a) => a.id);
    setAddressListSnapshot({ filter: addressFilter, search: addressSearch, addresses: aopAddresses, ids });
  }

  const filteredAddresses = addressListSnapshot
    ? aopAddresses.filter((a) => addressListSnapshot.ids.includes(a.id))
    : [];
  const totalPages = Math.max(1, Math.ceil(filteredAddresses.length / ADDRESS_PAGE_SIZE));
  const currentPage = Math.min(addressPage, totalPages - 1);
  const pageAddresses = filteredAddresses.slice(currentPage * ADDRESS_PAGE_SIZE, (currentPage + 1) * ADDRESS_PAGE_SIZE);

  let step1StatusText: string | undefined;
  if (connectError) {
    step1StatusText = t("page.integration.iloq.status.connection-failed");
  } else if (step1Done) {
    step1StatusText = t("page.integration.iloq.status.connected");
  }

  const filterOptions = [
    { value: "all" as AddressFilter, label: t("page.integration.iloq.step4.filter.all") },
    { value: "unmapped" as AddressFilter, label: t("page.integration.iloq.step4.filter.unmapped") },
  ] as const;

  const handleSubmit = (formValues: ILoqFormValues) => {
    saveMutation.mutate(
      {
        enabled: enabledOnSave,
        serviceAddress: formValues.serviceAddress,
        customerCode: formValues.customerCode,
        username: formValues.username,
        password: formValues.password || undefined,
        lockGroupId: formValues.lockGroup?.id,
        realEstateId: formValues.realEstate?.id,
        zoneId: formValues.zone?.id,
        supportUserId: formValues.supportUser?.id,
        securityAccessMappings: Object.entries(formValues.mappings ?? {}).map(([addressId, securityAccessIds]) => ({
          addressId,
          securityAccessIds,
        })),
      },
      { onSuccess: onSaved },
    );
  };

  return (
    <Form.Root formMethods={formMethods} onSubmit={handleSubmit}>
      <Stack gap="md">
        {/* 1. Service credentials */}
        <Form.Container title={`1. ${t("page.integration.iloq.step1.label")}`}>
          <Form.Body>
            <FormField label={t("page.integration.iloq.form.service-address")} required>
              <FormInput<ILoqFormValues, "serviceAddress">
                id="serviceAddress"
                name="serviceAddress"
                placeholder="https://your-instance.iloq.com"
              />
            </FormField>
            <Tiles columns={2} gap="md">
              <FormField label={t("page.integration.iloq.form.customer-code")} required>
                <FormInput<ILoqFormValues, "customerCode">
                  id="customerCode"
                  name="customerCode"
                  placeholder="ACME-NL"
                />
              </FormField>
              <FormField label={t("page.integration.iloq.form.username")} required>
                <FormInput<ILoqFormValues, "username">
                  id="username"
                  name="username"
                  placeholder="service@example.com"
                />
              </FormField>
            </Tiles>
            <FormField
              label={t("page.integration.iloq.form.password")}
              required
              description={hasSavedConfig ? t("page.integration.iloq.form.password-hint") : undefined}
            >
              <FormInput<ILoqFormValues, "password">
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
              />
            </FormField>
          </Form.Body>
          <Form.Footer
            renderTertiaryAction={
              step1StatusText
                ? () => (
                    <Badge variant={step1Done ? "success" : "danger"} icon={step1Done ? <CheckIcon /> : undefined}>
                      {step1StatusText}
                    </Badge>
                  )
                : undefined
            }
            renderPrimaryAction={() => (
              <Button
                variant="soft"
                onClick={handleConnect}
                isLoading={connectQuery.isFetching}
                disabled={!step1CanConnect}
              >
                {step1Done ? t("page.integration.iloq.action.reconnect") : t("page.integration.iloq.action.connect")}
              </Button>
            )}
          />
        </Form.Container>

        {/* 2. Lock group */}
        <section className={twJoin("transition-opacity", !step1Done && "pointer-events-none opacity-40")}>
          <Form.Container title={`2. ${t("page.integration.iloq.step2.label")}`}>
            <Form.Body>
              <FormField label={t("page.integration.iloq.form.lock-group")} required>
                <FormSelect<ILoqFormValues, ILoqOptionDto>
                  items={lockGroups ?? []}
                  renderOption={(g) => g.name}
                  keySelector={(g) => g.id}
                  emptyItem="— select —"
                  name="lockGroup"
                />
              </FormField>
            </Form.Body>
            <Form.Footer
              renderTertiaryAction={
                step2Done && lockGroup
                  ? () => (
                      <Badge variant="success" icon={<CheckIcon />}>
                        {lockGroup.name}
                      </Badge>
                    )
                  : undefined
              }
              renderPrimaryAction={() => (
                <Button
                  variant="soft"
                  onClick={() => optionsQuery.refetch()}
                  isLoading={optionsQuery.isFetching}
                  disabled={!lockGroup}
                >
                  {t("page.integration.iloq.action.reload")}
                </Button>
              )}
            />
          </Form.Container>
        </section>

        {/* 3. Real estate, zone & support user */}
        <section className={twJoin("transition-opacity", !step2Done && "pointer-events-none opacity-40")}>
          <Form.Container title={`3. ${t("page.integration.iloq.step3.label")}`}>
            <Form.Body>
              <Tiles columns={2} gap="md">
                <FormField label={t("page.integration.iloq.form.real-estate")} required>
                  <FormSelect<ILoqFormValues, ILoqOptionDto>
                    items={options?.realEstates ?? []}
                    renderOption={(r) => r.name}
                    keySelector={(r) => r.id}
                    emptyItem="— select —"
                    name="realEstate"
                  />
                </FormField>
                <FormField label={t("page.integration.iloq.form.zone")} required>
                  <FormSelect<ILoqFormValues, ILoqOptionDto>
                    items={options?.zones ?? []}
                    renderOption={(z) => z.name}
                    keySelector={(z) => z.id}
                    emptyItem="— select —"
                    name="zone"
                  />
                </FormField>
              </Tiles>
              <FormField label={t("page.integration.iloq.form.support-user")}>
                <FormSelect<ILoqFormValues, UserDto>
                  items={adminUsers}
                  renderOption={(u) => `${u.firstName} ${u.lastName}`}
                  keySelector={(u) => u.id}
                  emptyItem="— none —"
                  name="supportUser"
                />
              </FormField>
            </Form.Body>
            {step3Done && realEstate && (
              <Form.Footer
                renderTertiaryAction={() => (
                  <Badge variant="success" icon={<CheckIcon />}>
                    {realEstate.name}
                  </Badge>
                )}
                renderPrimaryAction={() => null}
              />
            )}
          </Form.Container>
        </section>

        {/* 4. Address mappings */}
        <section className={twJoin("transition-opacity", !step3Done && "pointer-events-none opacity-40")}>
          <Form.Container title={`4. ${t("page.integration.iloq.step4.label")}`}>
            <Form.Body>
              {totalAddresses > 0 && (
                <>
                  <Notice
                    variant={unmatchedCount === 0 ? "success" : "warning"}
                    icon={unmatchedCount === 0 ? <Stars01Icon /> : <AlertTriangleIcon />}
                    message={
                      unmatchedCount === 0
                        ? t("page.integration.iloq.step4.banner.all-matched", { total: totalAddresses })
                        : t("page.integration.iloq.step4.banner.partial-matched", {
                            matched: matchedCount,
                            total: totalAddresses,
                            unmatched: unmatchedCount,
                          })
                    }
                  />
                  <Inline gap="sm" align="center">
                    <ProgressBar progress={progressPercentage} thickness="sm" />
                    <span className="shrink-0 text-overline text-text-secondary">
                      {matchedCount} / {totalAddresses} {t("page.integration.iloq.step4.mapped")}
                    </span>
                  </Inline>
                </>
              )}

              <Inline gap="sm" align="center">
                <SearchInput
                  width="fluid"
                  placeholder={t("page.integration.iloq.step4.search")}
                  value={addressSearch}
                  onChange={(e) => {
                    setAddressSearch(e.target.value);
                    setAddressPage(0);
                  }}
                />
                <SegmentGroup
                  value={addressFilter}
                  items={filterOptions}
                  onChange={(value) => {
                    setUserAddressFilter(value);
                    setAddressPage(0);
                  }}
                />
              </Inline>

              <Card as="section" density="flush" clip>
                <header className="flex bg-background-secondary p-space-sm text-overline-emphasis text-text-secondary">
                  <span className="min-w-0 flex-1">{t("page.integration.iloq.step4.col.address")}</span>
                  <span className="flex-1">{t("page.integration.iloq.step4.col.security-access")}</span>
                </header>
                {pageAddresses.length === 0 ? (
                  <p className="px-space-sm py-space-md text-center text-caption text-text-tertiary">
                    {t("page.integration.iloq.step4.no-results")}
                  </p>
                ) : (
                  pageAddresses.map((address) => {
                    const selectedIds = (mappings ?? {})[address.id] ?? [];
                    const isUnmapped = selectedIds.length === 0;

                    return (
                      <section
                        key={address.id}
                        className={twJoin(
                          "flex items-center border-t border-border-default p-space-sm",
                          isUnmapped && "bg-background-warning-secondary",
                        )}
                      >
                        <span className="min-w-0 flex-1 text-caption text-text-primary">{address.location}</span>
                        <section className="flex-1">
                          <MultiSelect
                            usePortal
                            items={sortedAccessesByAddress.get(address.id) ?? securityAccesses}
                            selected={securityAccesses.filter((x) => selectedIds.includes(x.id))}
                            renderOption={(x) => x.name}
                            keySelector={(x) => x.id}
                            placeholder="— select —"
                            aria-invalid={isUnmapped}
                            onChange={(items) => {
                              const nextMappings = { ...(mappings ?? {}) };
                              if (items.length > 0) {
                                nextMappings[address.id] = items.map((x) => x.id);
                              } else {
                                delete nextMappings[address.id];
                              }
                              setValue("mappings", nextMappings);
                            }}
                          />
                        </section>
                      </section>
                    );
                  })
                )}
              </Card>

              {totalPages > 1 && (
                <Spread>
                  <span className="text-overline text-text-secondary">
                    {filteredAddresses.length} {t("page.integration.iloq.step4.address-count")}
                    {addressFilter === "unmapped" && ` ${t("page.integration.iloq.step4.remaining")}`}
                  </span>
                  <Pagination count={totalPages} currentIndex={currentPage} onChange={setAddressPage} />
                </Spread>
              )}
            </Form.Body>
          </Form.Container>
        </section>

        {step3Done && (
          <Form.Footer
            renderPrimaryAction={() => (
              <Button type="submit" isLoading={saveMutation.isPending}>
                {submitLabel ?? t("common.action.save")}
              </Button>
            )}
          />
        )}
      </Stack>
    </Form.Root>
  );
}

function normalizeIloq(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const RESIDENT_ACCESS_NAMES = ["resident", "bewoner", "residents", "bewoners"];
// Well above any house-number match score so the "Resident"/"Bewoner" access — which applies to
// every unit — always ranks first in a per-address dropdown.
const RESIDENT_SCORE = 10_000;

function isResidentAccess(securityAccess: ILoqOptionDto): boolean {
  return RESIDENT_ACCESS_NAMES.includes(normalizeIloq(securityAccess.name));
}

// The forms a house number can take inside an iLOQ security-access name. iLOQ zero-pads house
// numbers (e.g. "050" for number 50), so we consider both the raw and the 3-digit zero-padded
// form. Matching these as whole words avoids e.g. number 5 colliding with the "5" inside "050".
function houseNumberVariants(address: AddressDto): string[] {
  const suffix = normalizeIloq(address.houseNumberSuffix || "");
  const raw = normalizeIloq(String(address.houseNumber));
  if (!raw) {
    return [];
  }

  const numbers = new Set([raw]);
  if (/^\d+$/.test(raw)) {
    numbers.add(raw.padStart(3, "0"));
  }

  return [...numbers].flatMap((n) => (suffix ? [n + suffix, n] : [n]));
}

// How likely a security access belongs to a given address. Higher is better; 0 means no
// house-number signal at all. Shared by the prematcher (picks the single best) and the per-row
// dropdown sort (orders every option most-likely first).
function scoreSecurityMatch(address: AddressDto, securityAccess: ILoqOptionDto): number {
  if (isResidentAccess(securityAccess)) {
    return RESIDENT_SCORE;
  }

  // Anything after a "/" is a unit/apartment number, not part of the house number or street
  // name — matching against it risks false positives (e.g. house number "10" colliding with
  // the "10" inside a unit number like "5010").
  const name = normalizeIloq(securityAccess.name.split("/")[0]);
  const words = name.split(" ");

  const variants = houseNumberVariants(address);
  let houseScore: number;
  if (variants.some((v) => words.includes(v))) {
    houseScore = 2; // whole-word match (raw or zero-padded) — strong signal
  } else if (variants.some((v) => name.includes(v))) {
    houseScore = 1; // substring match only — weaker, kept for backwards compatibility
  } else {
    return 0;
  }

  const streetWords = normalizeIloq(address.streetName)
    .split(" ")
    .filter((w) => w.length > 3);
  const overlap = streetWords.filter((w) => words.some((sw) => sw.startsWith(w) || w.startsWith(sw))).length;

  // +1 keeps a bare house-number match (overlap 0) above the 0 returned for no match at all.
  return houseScore * 100 + overlap + 1;
}

function prematch(addresses: AddressDto[], secAccesses: ILoqOptionDto[]): Record<string, string[]> {
  // The "Resident"/"Bewoner" access always applies to every unit, on top of whatever the
  // house-number match finds — it's not an alternative to it.
  const residentAccess = secAccesses.find(isResidentAccess);

  const result: Record<string, string[]> = {};

  for (const address of addresses) {
    let best: ILoqOptionDto | null = null;
    let bestScore = 0;

    for (const securityAccess of secAccesses) {
      if (isResidentAccess(securityAccess)) {
        continue;
      }

      const score = scoreSecurityMatch(address, securityAccess);
      if (score > bestScore) {
        bestScore = score;
        best = securityAccess;
      }
    }

    const matches = new Set<string>();
    if (residentAccess) {
      matches.add(residentAccess.id);
    }
    if (best) {
      matches.add(best.id);
    }

    if (matches.size > 0) {
      result[address.id] = [...matches];
    }
  }

  return result;
}
